import { useState } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'next-i18next'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { KeyedMutator } from 'swr/dist/_internal'
import { TARGET_COMMENT_ID_QUERY_PARAM } from 'shared/constants/comments'
import useUser from 'shared/hooks/use-user'
import { PostAttachmentsProvider } from 'modules/attachments/components/context'
import { likePost, unlikePost } from 'modules/community/api/community-api'
import { declinePost, deletePost } from 'modules/community/api/posts-api'
import * as postsApi from 'modules/community/api/posts-api'
import { usePublishPostsMultiple } from 'modules/community/api/use-publish-posts-multiple'
import { PostAnnounceSkeleton } from 'modules/community/components/post-announce/components/post-announce-skeleton'
import { ACTIVITY_CLEANUP_PERIOD_DURATION } from 'modules/community/const/post-consts'
import { BanPeriodUnitEnum } from 'modules/community/enums/member-enum'
import { PostStatusEnum } from 'modules/community/enums/post-status-enum'
import { PostInterface } from 'modules/community/types/post-interface'
import { PostView } from './components/post-view'

type PostDetailsProps = {
  post: PostInterface
  mutatePost: KeyedMutator<PostInterface>
}

export interface CommentInterface {
  id: number
  created_at: number
  user_name: string
  user_avatar_url: string
  text: string
  total_replies: number
  can_delete_comment: boolean
}

const PostDetails = ({ post, mutatePost }: PostDetailsProps) => {
  const router = useRouter()
  const { communityPath, [TARGET_COMMENT_ID_QUERY_PARAM]: targetCommentId } = router.query as {
    communityPath: string
    [TARGET_COMMENT_ID_QUERY_PARAM]?: string
  }
  const [isLoading, setIsLoading] = useState(false)

  const { user, loading: isUserLoading } = useUser()
  const { t } = useTranslation()

  const publishMultiplePostsMutation = usePublishPostsMultiple()

  const handleDeletePost = (postId: number) => {
    return async () => {
      await deletePost(postId)
      await router.push(`/community/${communityPath}`)
    }
  }

  const handleLikeClick = async () => {
    try {
      if (post.isLiked) {
        await unlikePost(post.id)
        await mutatePost(
          data => data && { ...data, isLiked: false, likesCount: data.likesCount - 1 },
          false,
        )
      } else {
        await likePost(post.id)
        await mutatePost(
          data => data && { ...data, isLiked: true, likesCount: data.likesCount + 1 },
          false,
        )
      }
    } catch (err) {
      toast.error(t('post_details.error.error_like_post'))
    }
  }

  const handleApprovePost = async () => {
    try {
      await publishMultiplePostsMutation.trigger({ ids: [post.id] })
      await mutatePost(post => post && { ...post, status: PostStatusEnum.Approved }, false)
    } catch (err) {
      toast.error(t('post_details.error.error_approve_post'))
    }
  }

  const handleDecliePost = async () => {
    setIsLoading(true)
    await declinePost(communityPath as string, [post.id])
    await mutatePost(post => post && { ...post, status: PostStatusEnum.Declined }, false)
    setIsLoading(false)
  }

  if (!user || isUserLoading) {
    return <PostAnnounceSkeleton />
  }

  const deletePostAndBanUser =
    (postId: number, userId: number) => async (removeRecentActivitiesChecked: boolean) => {
      await postsApi.banUser(
        communityPath as string,
        userId,
        removeRecentActivitiesChecked
          ? {
              deleteRecentActivityOptions: {
                periodUnit: BanPeriodUnitEnum.day,
                periodDuration: ACTIVITY_CLEANUP_PERIOD_DURATION,
              },
            }
          : undefined,
      )
      await postsApi.deletePost(postId)
      await router.push(`/community/${communityPath}`)
    }

  return (
    <PostAttachmentsProvider attachments={post.attachments}>
      <Head>
        <title>{post.title}</title>
      </Head>
      <div className="mt-6 bg-background-secondary lg:mt-0 lg:rounded-lg">
        <PostView
          post={post}
          handleApprovePost={handleApprovePost}
          handleDecliePost={handleDecliePost}
          handleDeletePost={handleDeletePost(post.id)}
          handleLikeClick={handleLikeClick}
          deletePostAndBanUser={deletePostAndBanUser(post.id, post.userId)}
          isLoading={isLoading || publishMultiplePostsMutation.isMutating}
          targetCommentId={targetCommentId ? Number(targetCommentId) : undefined}
        />
      </div>
    </PostAttachmentsProvider>
  )
}

export default PostDetails
